import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import {
  formatCalendarDateLong,
  formatCalendarDateMedium,
  formatLocalizedDateTime,
  formatLocalizedTime,
} from "./dateFormatting";

dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(utc);

export const shortDayFormat = "YYYY-MM-DD";
export const mediumDayFormat = "MMM D, YYYY";
export const longDayFormat = "MMMM D, YYYY";

/** API instant → localized date + time (viewer locale and timezone). */
export const formatBEDateToDateTimeString = (dateString: string | null | undefined) => {
  return formatLocalizedDateTime(dateString);
};

/** @deprecated Prefer {@link formatBEDateToDateTimeString} — kept for call-site compatibility. */
export const formatBEDateToDateTimeAtString = (dateString: string | null | undefined) => {
  return formatLocalizedDateTime(dateString);
};

export const formatBEDateToTimeString = (dateString: string | null | undefined) => {
  return formatLocalizedTime(dateString);
};

export const formatBEDateToShortDateString = (dateString: string) => {
  const dateOnlyMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnlyMatch) {
    return dateOnlyMatch[1];
  }
  return formatBEDateToDateString(dateString, shortDayFormat);
};

/** Parses API calendar dates (YYYY-MM-DD or legacy ISO) without local-day drift. */
export const parseCalendarDate = (dateString: string) =>
  dayjs(formatBEDateToShortDateString(dateString), shortDayFormat, true);

/** Calendar or instant date, medium style, viewer locale. */
export const formatBEDateToMediumDateString = (dateString: string | null | undefined) => {
  return formatCalendarDateMedium(dateString);
};

export const formatBEDateToLongDateString = (dateString: string | null | undefined) => {
  return formatCalendarDateLong(dateString);
};

export const formatBEDateToMediumDayString = (dateString: string) => {
  return formatLocalizedDateTime(dateString);
};

export const formatBEDateToLongDayString = (dateString: string) => {
  return formatLocalizedDateTime(dateString);
};

/** @internal Legacy dayjs UTC formatter — do not use for new UI; prefer `dateFormatting` helpers. */
export const formatBEDateToDateString = (dateString: string | null | undefined, format: string) => {
  if (dateString === null || dateString === undefined) {
    return "";
  }

  const date = dayjs(dateString);
  if (!date.isValid()) {
    return "";
  }

  return dayjs.utc(dateString).format(format);
};

/** Local Date at noon for calendar-only values (shoot days); avoids ISO midnight drift. */
export const calendarDateToLocalDate = (dateString: string): Date => {
  const normalized = formatBEDateToShortDateString(dateString);
  if (!normalized) {
    return new Date();
  }
  const [year, month, day] = normalized.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export const formatBEDateToDate = (dateString: string | null | undefined) => {
  if (dateString === null || dateString === undefined) {
    return new Date();
  }

  return calendarDateToLocalDate(dateString);
};

export const formatHowLongAgo = (date: string): string => {
  if (!date) return "";

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return "";

  if (dayjsDate.isToday()) {
    return "Today";
  } else if (dayjsDate.isYesterday()) {
    return "Yesterday";
  }
  const today = dayjs().startOf("day");
  const diffDays = today.diff(dayjsDate.startOf("day"), "day");
  return `${diffDays} days ago`;
};

/** @deprecated Prefer {@link formatLocalizedDateTime}. */
export const formatBEUtcToLocal = (dateString: string | null | undefined, format: string) => {
  if (dateString === null || dateString === undefined) {
    return "";
  }

  const date = dayjs(dateString);
  if (!date.isValid()) {
    return "";
  }

  return dayjs.utc(dateString).local().format(format);
};

export const formatBEUtcToLocalDateTimeAtString = (dateString: string | null | undefined) => {
  return formatLocalizedDateTime(dateString);
};

export const formatDateToYYYYMMDD = (date: Date) => {
  const ndate = new Date(date);
  const year = ndate.getFullYear();
  const month = String(ndate.getMonth() + 1).padStart(2, "0");
  const day = String(ndate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Validates if a date string is valid and has a proper day value (not 0)
 */
export const isValidDateString = (dateStr: string): boolean => {
  if (!dateStr || typeof dateStr !== "string") return false;

  if (dateStr.endsWith("-00") || dateStr.includes("-00-")) return false;

  if (dateStr.trim() === "") return false;

  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;

  const dayjsDate = dayjs(dateStr);
  if (!dayjsDate.isValid()) return false;

  const day = dayjsDate.date();
  if (day === 0) return false;

  const formatted = dayjsDate.format("YYYY-MM-DD");
  if (formatted !== dateStr) return false;

  return true;
};

/**
 * Safely formats a validated YYYY-MM-DD calendar string for display.
 * Prefer {@link formatCalendarDateMedium} for API-backed dates.
 */
export const safeFormatDate = (dateStr: string, format: string = "MMM D, YYYY"): string => {
  if (!isValidDateString(dateStr)) {
    if (dateStr && dateStr.trim() !== "" && dateStr.length > 0) {
      console.warn(`Invalid date string detected: ${dateStr}`);
    }
    return dateStr;
  }

  if (format === mediumDayFormat || format === "MMM D, YYYY") {
    return formatCalendarDateMedium(dateStr);
  }
  if (format === longDayFormat || format === "MMMM D, YYYY") {
    return formatCalendarDateLong(dateStr);
  }

  try {
    return dayjs(dateStr).format(format);
  } catch (error) {
    console.error(`Error formatting date ${dateStr}:`, error);
    return dateStr;
  }
};
